import { ContentCopy } from '@mui/icons-material';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import { Grid } from '@mui/material';
import { dateFormat, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import styled from 'styled-components';

import CrossIcon from '../../../assets/svg/CrossIcon';
import { PlannerCredentials } from '../../../domain/credentials/PlannerCredentials';
import { Translation } from '../../../domain/translation/Translation';
import { intl } from '../../../helpers/IntGlobalProvider';
import Text from '../../../react-components/Text';
import { AlignmentTypes, DataTableColumnProps } from '../../../react-components/data-table/DataTable';
import { TranslationsTableColumn } from '../context/tableColumns/translations';

const StyledDeleteIcon = styled(CrossIcon)`
  width: 15px !important;
  height: 15px !important;
  font-size: 15px !important;
  color: #393939;
  padding: 4px;
  cursor: pointer;
`;

const DeleteButton = ({ onDelete }: { onDelete?: (...args: any[]) => any }) => {
  return <StyledDeleteIcon onClick={onDelete} />;
};

type TranslationsGetColumnsResponse = { [key: string]: any }[];

type TranslationsGetColumns = {
  userCredentials?: PlannerCredentials;
  tableColumns: Array<TranslationsTableColumn>;
  setTranslationToDelete: (translation: Translation) => void;
  onEditTranslation: (translation: Translation) => void;
};

export const getTranslationsColumns = ({
  userCredentials,
  tableColumns,
  setTranslationToDelete,
  onEditTranslation,
}: TranslationsGetColumns): TranslationsGetColumnsResponse => {
  const columns: Array<DataTableColumnProps> = [];

  tableColumns.forEach(({ displayable, identifier, sortable }: TranslationsTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `key`:
          columns.push({
            width: 330,
            columnStyle: {
              width: `unset`,
            },
            accessorName: `key`,
            align: `justify` as AlignmentTypes,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="administration.translations.identifier" />
            ),
            formatter: ({ value: key, rowHover }) => (
              <Grid container direction="row" alignItems="flex-start" justifyContent="space-between">
                <RoutalTooltip title={key}>
                  <Text style={{ width: `300px` }}>{key}</Text>
                </RoutalTooltip>

                <HSpacer small />

                {rowHover ? (
                  <ContentCopy
                    style={{
                      fontSize: `16px`,
                      color: RoutalPalette.secondary00,
                      cursor: `pointer`,
                    }}
                    onClick={(e: any) => {
                      const el = document.createElement(`textarea`);
                      el.value = key;
                      const helper = document.getElementById(`copy-translation-key-helper`);
                      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
                      helper.appendChild(el);
                      el.select();
                      document.execCommand(`copy`);
                      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
                      helper.removeChild(el);
                    }}
                  />
                ) : null}
              </Grid>
            ),
          });
          break;
        case `original_value`:
          columns.push({
            accessorName: `original_value`,
            sortable,
            width: 350,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="administration.translations.original_value"
              />
            ),
            formatter: ({ value: originalValue }) => (
              <Text
                tagName="div"
                variant="body"
                style={{
                  background: RoutalPalette.neutral02,
                  borderRadius: `4px`,
                  height: `100%`,
                  minHeight: `34px`,
                  padding: `8px`,
                  width: `350px`,
                }}
              >
                {originalValue}
              </Text>
            ),
          });
          break;
        case `value`:
          columns.push({
            accessorName: `value`,
            sortable,
            valueCellStyle: {
              width: `350px`,
            },
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="administration.translations.value" />,
            formatter: ({ doc: translation }: { doc: Translation }) => {
              return (
                <Text
                  tagName="div"
                  variant="body"
                  style={{
                    background: RoutalPalette.neutral00,
                    borderRadius: `4px`,
                    height: `100%`,
                    minHeight: `34px`,
                    padding: `8px`,
                    width: `350px`,
                    cursor: `pointer`,
                  }}
                  onClick={() => {
                    onEditTranslation(translation);
                  }}
                >
                  {translation.value}
                </Text>
              );
            },
          });
          break;
        case `auto_translate`:
          columns.push({
            accessorName: `auto_translate`,
            sortable,
            width: 20,
            name: ``,
            formatter: ({ doc: translation }: { doc: Translation }) => {
              return (
                <>
                  {translation.auto_translate && (
                    <RoutalTooltip title="Automatically generated translation">
                      <Grid
                        key={`${translation.key}-row`}
                        item
                        style={{
                          width: `20px`,
                          height: `20px`,
                        }}
                      >
                        <GTranslateIcon style={{ color: `#eed202` }} />
                      </Grid>
                    </RoutalTooltip>
                  )}
                </>
              );
            },
          });
          break;
        default:
          break;
      }
    }
  });

  return [
    ...columns,
    ...(tableColumns.find(({ displayable, identifier }) => identifier === `created_at` && displayable)
      ? [
          {
            width: 20,
            accessorName: `created_at`,
            align: `center` as AlignmentTypes,
            sortable: true,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="administration.translations.created_at" />
            ),
            formatter: ({ value: createdAt }) => (
              <RoutalTooltip title={dateFormat(createdAt)}>
                <Text variant="body">{pastDateTime(createdAt)}</Text>
              </RoutalTooltip>
            ),
          } as DataTableColumnProps,
        ]
      : []),
    ...(userCredentials?.test(`can_delete_translations`)
      ? [
          {
            align: `right`,
            // width: 20,
            height: 28,
            // flex: 1,
            formatter: ({ doc: translation, rowHover }) => (
              <Grid container justifyContent="flex-end" style={{ height: `28px` }}>
                {rowHover ? (
                  <Grid
                    key={`${translation.key}-actions`}
                    item
                    container
                    alignItems="center"
                    justifyContent="center"
                    // style={{ width: `20px` }}
                  >
                    <RoutalTooltip title={intl.formatMessage({ id: `administration.translations.delete_translation` })}>
                      <DeleteButton
                        onDelete={() => {
                          setTranslationToDelete(translation);
                        }}
                      />
                    </RoutalTooltip>
                  </Grid>
                ) : null}
              </Grid>
            ),
          } as DataTableColumnProps,
        ]
      : []),
  ] as Array<DataTableColumnProps>;
};
